import { get } from 'lodash';

import { reportErrorInProductionOrThrow } from '../../../../../Utils';

/**
 * The covered_object_id the FNOL payload carries to mark a first party as not listed on the policy.
 * Mirrors auto's unlisted-covered-vehicle sentinel and the server's
 * general_claims_manager.NOT_LISTED_ON_POLICY_COVERED_OBJECT_ID.
 */
export const NOT_LISTED_ON_POLICY_COVERED_OBJECT_ID = 0;

/**
 * Whether a party config is explicitly unscheduled (unlisted) — the mirror of the server's
 * lob_configuration.party_utils.is_explicitly_unscheduled_party.
 *
 * A present covered_object_type of null or '' marks unscheduled; an absent key is a legacy
 * scheduled party. '' is grouped with null because the LOB validators treat it as "no link"
 * too, so a config seeded outside the admin API (fixture / CR import) must not have FNOL
 * demand a covered object the backend never requires.
 *
 * Keep in sync with server/lob_configuration/party_utils.py.
 *
 * @param {Object} partyConfig - A single involved_parties entry from the incident configuration
 * @returns {boolean}
 */
export const isExplicitlyUnscheduledParty = (partyConfig) =>
  !!partyConfig &&
  'covered_object_type' in partyConfig &&
  (partyConfig.covered_object_type === null || partyConfig.covered_object_type === '');

/**
 * Gets eligible covered objects for an involved party type.
 *
 * @param {string} partyTypeKey - The involved party type key (e.g., 'involved_generic_person')
 * @param {string} lob - Line of business (used only for diagnostics)
 * @param {Object} policy - Policy object with covered_objects array (actual policy data with integer IDs)
 * @param {Object} incidentConfiguration - Incident configuration object (schema with string IDs for lookup)
 * @returns {Object} Object with:
 *   - coveredObjects: Array of filtered covered objects from the policy
 *   - coveredObjectType: string|null - The type used for filtering (matches policy covered object's type field)
 *   - coveredObjectDesc: string - Description for display (from incident configuration)
 *   - allowsNotListedOnPolicy: boolean - Whether this party may be marked as not listed on the policy,
 *     read off the resolved covered object type's allow_not_listed_on_policy, which only allows it
 *     when it is exactly `true`. False whenever no type resolves, mirroring the server refusing
 *     there. Keep in sync with
 *     server/lob_configuration/covered_object_utils.is_not_listed_on_policy_allowed.
 */
export const getEligibleCoveredObjects = (partyTypeKey, lob, policy, incidentConfiguration = null) => {
  // Manual policies don't require covered object validation
  if (!policy || policy.is_manual) {
    const allCoveredObjects = policy?.covered_objects || [];
    return {
      coveredObjects: allCoveredObjects,
      coveredObjectType: null,
      coveredObjectDesc: 'Covered Object',
      isPolicyListed: true,
      allowsNotListedOnPolicy: false,
    };
  }

  // Read the party config from the org's incident configuration — the same authoritative,
  // per-organization source the backend enforcement (fnol_manager, party_utils) and
  // InvolvedGenericFragment read. The global LobConfiguration template is shared across all
  // orgs and can diverge from an org's incident config, so reading is_first_party /
  // covered_object_type from it would make the covered-object dropdown disagree with what the
  // backend requires. involved_parties here is a dict keyed by party id.
  const involvedPartiesConfig = get(incidentConfiguration, 'involved_parties', {});
  const lobPartyConfig = involvedPartiesConfig[partyTypeKey];

  // Third parties don't require covered objects
  if (!lobPartyConfig?.is_first_party) {
    const allCoveredObjects = policy?.covered_objects || [];
    return {
      coveredObjects: allCoveredObjects,
      coveredObjectType: null,
      coveredObjectDesc: 'Covered Object',
      isPolicyListed: true,
      allowsNotListedOnPolicy: false,
    };
  }

  // Explicitly unscheduled (unlisted) first parties don't have a covered object link. A legacy
  // party with an absent covered_object_type stays scheduled and keeps the fallback below.
  const configuredCoveredObjectTypeId = lobPartyConfig?.covered_object_type;
  if (isExplicitlyUnscheduledParty(lobPartyConfig)) {
    return {
      coveredObjects: [],
      coveredObjectType: null,
      coveredObjectDesc: 'Covered Object',
      isPolicyListed: false,
      // No covered object type, so nothing carries the setting — and the dropdown this would
      // appear in is not rendered for an unscheduled party anyway.
      allowsNotListedOnPolicy: false,
    };
  }

  // policy.covered_objects contains actual policy data (id is integer, type is string like "member")
  const policyCoveredObjects = policy?.covered_objects || [];
  // incidentConfiguration.policy.covered_objects contains schema/config (id is string like "member", has desc)
  const incidentCoveredObjectsConfig = incidentConfiguration?.policy?.covered_objects || [];
  let coveredObjectType = null;
  let coveredObjectDesc = 'Covered Object';
  // The resolved type's own config entry, which carries allow_not_listed_on_policy. Stays null in
  // case 3 below, where no single type resolves.
  let resolvedCoveredObjectConfig = null;

  // 1. If configured covered_object_type exists, look up the config to get desc
  // Note: configuredCoveredObjectTypeId is a string ID like "member" that matches:
  //   - incidentConfiguration.policy.covered_objects[n].id (for getting desc)
  //   - policy.covered_objects[n].type (for filtering actual policy covered objects)
  if (configuredCoveredObjectTypeId) {
    // Look up from incident configuration (has string IDs) to get the description
    const coveredObjectConfig = incidentCoveredObjectsConfig.find((obj) => obj.id === configuredCoveredObjectTypeId);
    // The configured ID is the type we use for filtering policy covered objects
    coveredObjectType = configuredCoveredObjectTypeId;
    coveredObjectDesc = coveredObjectConfig?.desc || 'Covered Object';
    resolvedCoveredObjectConfig = coveredObjectConfig;
  }
  // 2. If not configured and there is only one type of covered object in the config, use it
  else if (incidentCoveredObjectsConfig.length === 1) {
    const singleCoveredObjectConfig = incidentCoveredObjectsConfig[0];
    coveredObjectType = singleCoveredObjectConfig.id;
    coveredObjectDesc = singleCoveredObjectConfig.desc || 'Covered Object';
    resolvedCoveredObjectConfig = singleCoveredObjectConfig;
  }
  // 3. If multiple types and no configured type, log error/warning and use all covered objects
  else if (incidentCoveredObjectsConfig.length > 1) {
    const errorMessage = `Missing covered_object_type for involved party "${partyTypeKey}" in LOB "${lob}" with ${incidentCoveredObjectsConfig.length} covered objects. Please configure covered_object_type.`;
    reportErrorInProductionOrThrow(errorMessage);
    // Use default fallback - will return all covered objects
    coveredObjectDesc = 'Covered Object';
  }

  // Filter policy covered objects based on the type
  // policy.covered_objects have type field that matches the config's id field
  const filteredCoveredObjects = coveredObjectType
    ? policyCoveredObjects.filter((obj) => obj.type === coveredObjectType)
    : policyCoveredObjects;

  return {
    coveredObjects: filteredCoveredObjects,
    coveredObjectType,
    coveredObjectDesc,
    isPolicyListed: true,
    // False in case 3, where several types are configured and the party declares no link: the
    // server refuses to guess a type there, so offering the option would build a payload it rejects.
    // Only `true` allows it: the configuration is served as the raw dict it was stored as, so the
    // setting can arrive as the string 'false' — truthy, yet the server reads it as not allowed.
    allowsNotListedOnPolicy: resolvedCoveredObjectConfig?.allow_not_listed_on_policy === true,
  };
};
